import {
    Card,
    Grid,
    Icon,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from "@mui/material";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import Loading from "components/Loading";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import dayjs from 'dayjs';
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import PieChart from "examples/Charts/PieChart";
import React, { useMemo, useState } from "react";
import { useAggregatedReport, useVendorPerformanceReport } from "api/report";
import RideSelector from "./RideSelector";
import VendorSelector from "./VendorSelector";


// Section Header Component
const SectionHeader = ({ title, subtitle }) => (
    <Grid item xs={12}>
        <MDBox mb={2} mt={2}>
            <MDBox display="flex" alignItems="center" mb={1}>
                <MDBox>
                    <MDTypography variant="h5" fontWeight="bold" color="dark">
                        {title}
                    </MDTypography>
                    {subtitle && (
                        <MDTypography variant="body2" color="text">
                            {subtitle}
                        </MDTypography>
                    )}
                </MDBox>
            </MDBox>
        </MDBox>
    </Grid>
);



// Work Order Progress Component
const WorkOrderProgressChart = React.memo(({ data, isLoading }) => {
    const chartData = useMemo(() => {
        if (!data || !data.by_progress || !Array.isArray(data.by_progress)) {
            return {
                labels: [],
                datasets: {
                    label: "Work Orders",
                    data: [],
                    backgroundColors: ["info", "warning", "success", "error"],
                }
            };
        }

        const labels = data.by_progress.map(item => item.name);
        const chartValues = data.by_progress.map(item => item.count);

        return {
            labels,
            datasets: {
                label: "Work Orders",
                data: chartValues,
                backgroundColors: [
                    "info",     // Blue - Open
                    "warning",  // Orange - On Hold
                    "primary",  // Purple - In Progress
                    "error",    // Red - In Review
                    "secondary", // Purple - Waiting For Parts
                    "dark",     // Dark - Waiting For Cost
                    "light",  // Light - Waiting For Invoice
                    "success"   // Green - Completed
                ],
            }
        };
    }, [data]);

    if (isLoading) {
        return (
            <Card sx={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Loading size={40} />
            </Card>
        );
    }

    return (
        <PieChart
            icon={{ color: "info", component: "engineering" }}
            title="Work Orders Progress"
            description="Current work order progress distribution"
            height="22rem"
            chart={chartData}
        />
    );
});

// Inventory Parts Table Component
const InventoryPartsTable = React.memo(({ data, isLoading }) => {
    const tableData = useMemo(() => {
        if (!data || !data.by_tag) return [];

        return Object.values(data.by_tag)
            .sort((a, b) => b.total_stock - a.total_stock) // Sort by stock descending
    }, [data]);

    if (isLoading) {
        return (
            <Card sx={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
                <Loading size={40} />
            </Card>
        );
    }

    return (
        <Card sx={{ height: "100%" }}>
            <MDBox display="flex" alignItems="center" pt={2} px={2} pb={1}>
                <MDBox
                    variant="gradient"
                    bgColor="info"
                    color="white"
                    coloredShadow="info"
                    borderRadius="xl"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width="4rem"
                    height="4rem"
                    mt={-3}
                    mr={2}
                >
                    <Icon fontSize="medium" color="inherit">inventory_2</Icon>
                </MDBox>
                <MDBox>
                    <MDTypography variant="h6" textTransform="capitalize" color="dark">
                        Spare Parts Usage
                    </MDTypography>
                    <MDBox mb={2}>
                        <MDTypography component="div" variant="button" color="text">
                            Current parts usage by tag
                        </MDTypography>
                    </MDBox>
                </MDBox>
            </MDBox>

            {/* Table */}
            <MDBox px={2} pb={2}>
                <TableContainer component={Paper} sx={{ maxHeight: 400, boxShadow: 1, width: '100%' }}>
                    <Table stickyHeader size="small">
                        <TableHead style={{ display: "table-header-group" }}>
                            <TableRow>
                                <TableCell><strong>Tag</strong></TableCell>
                                <TableCell align="center"><strong>Total Stock</strong></TableCell>
                                <TableCell align="center"><strong>Parts Count</strong></TableCell>
                                <TableCell align="center"><strong>Quantity Used</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableData.length > 0 ? (
                                tableData.map((part, index) => (
                                    <TableRow
                                        key={index}
                                        sx={{
                                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                                            '&:last-child td': { border: 0 }
                                        }}
                                    >
                                        <TableCell>
                                            <MDTypography variant="body2" fontWeight="medium">
                                                {part.tag}
                                            </MDTypography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <MDTypography
                                                variant="body2"
                                                fontWeight="medium"
                                            >
                                                {part.total_stock}
                                            </MDTypography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <MDTypography variant="body2" color="text">
                                                {part.parts_count}
                                            </MDTypography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <MDTypography
                                                variant="body2"
                                                fontWeight={part.quantity_used > 0 ? "medium" : "regular"}
                                            >
                                                {part.quantity_used}
                                            </MDTypography>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                        <MDTypography variant="body2" color="text">
                                            No inventory data available
                                        </MDTypography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </MDBox>
        </Card>
    );
});

// Main Dashboard Component
const DashboardOverview = () => {
    // Date filter state
    const [startDate, setStartDate] = useState(dayjs().subtract(30, 'day'));
    const [endDate, setEndDate] = useState(dayjs());
    const [rideId, setRideId] = useState(null);
    // (Global) vendor selector removed from top-level filters; vendor performance has its own filters

    // Vendor performance specific filters
    const [vendorStartDate, setVendorStartDate] = useState(dayjs().subtract(60, 'day'));
    const [vendorEndDate, setVendorEndDate] = useState(dayjs());
    const [vendorFilter, setVendorFilter] = useState(null); // single-select for vendor performance

    const isDateRangeValid = useMemo(() => {
        return startDate && endDate && (startDate.isBefore(endDate) || startDate.isSame(endDate, 'day'));
    }, [startDate, endDate]);

    const isVendorDateRangeValid = useMemo(() => (
        vendorStartDate && vendorEndDate && (vendorStartDate.isBefore(vendorEndDate) || vendorStartDate.isSame(vendorEndDate, 'day'))
    ), [vendorStartDate, vendorEndDate]);

    const dashboardQuery = useAggregatedReport({
        start_date: startDate.format('YYYY-MM-DD'),
        end_date: endDate.format('YYYY-MM-DD'),
        ...(rideId ? { ride_id: rideId } : {})
    }, {
        enabled: isDateRangeValid,
    });

    const vendorPerformanceQuery = useVendorPerformanceReport({
        start_date: vendorStartDate.format('YYYY-MM-DD'),
        end_date: vendorEndDate.format('YYYY-MM-DD'),
        ...(vendorFilter ? { vendor_id: vendorFilter?.id } : {})
    }, {
        enabled: isVendorDateRangeValid,
    });

    const dashboardData = dashboardQuery.data;
    const isLoading = dashboardQuery.isFetching;
    const vendorPerfData = vendorPerformanceQuery.data;
    const vendorPerfLoading = vendorPerformanceQuery.isFetching;

    const formatCurrency = useMemo(() => {
        return (value) => {
            return new Intl.NumberFormat('en-MY', {
                style: 'currency',
                currency: 'MYR',
                minimumFractionDigits: 2,
            }).format(value);
        };
    }, []);

    // Memoize KPI values
    const kpiValues = useMemo(() => ({
        totalWorkOrders: isLoading ? "-" : dashboardData?.work_orders?.total || 0,
        waitingForParts: isLoading ? "-" : dashboardData?.work_orders?.waiting_for_parts || 0,
        completedWorkOrders: isLoading ? "-" : dashboardData?.work_orders?.completed || 0,
        overdueOrders: isLoading ? "-" : dashboardData?.work_orders?.overdue || 0,
        dueTodayOrders: isLoading ? "-" : dashboardData?.work_orders?.due_today || 0,
        dueSoonOrders: isLoading ? "-" : dashboardData?.work_orders?.due_soon || 0,
        criticalOpenOrders: isLoading ? "-" : dashboardData?.work_orders?.critical_open || 0,
        avgCompletionHours: isLoading ? "-" : dashboardData?.work_orders?.avg_completion_hours || 'N/A',
        totalSpareparts: isLoading ? "-" : dashboardData?.inventory?.parts_count || 0,
        totalStockValue: isLoading ? "-" : formatCurrency(parseFloat(dashboardData?.inventory?.total_stock_value || 0)),
        belowMinimum: isLoading ? "-" : dashboardData?.inventory?.below_minimum || 0,
        outOfStock: isLoading ? "-" : dashboardData?.inventory?.out_of_stock || 0,
        totalUsageEvents: isLoading ? "-" : Object.keys(dashboardData?.sparepart_usage?.by_tag || {}).length || 0,
        totalQuantityUsed: isLoading ? "-" : Object.values(dashboardData?.sparepart_usage?.by_tag || {}).reduce((sum, item) => sum + (item?.quantity_used || 0), 0) || 0,
    }), [isLoading, dashboardData, formatCurrency]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MDBox sx={{ mt: -8 }} mb={4}>
                <Grid container spacing={4} mb={3}>
                    <Grid item xs={12}>
                        <Card sx={{ p: 2 }}>
                            <MDBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                                <MDBox>
                                    <MDTypography variant="h6" fontWeight="medium" color="dark" mb={0.5}>
                                        Dashboard Filters
                                    </MDTypography>
                                    <MDTypography variant="body2" color="text">
                                        Select date range to filter dashboard data
                                    </MDTypography>
                                </MDBox>
                                <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap" sx={{ flex: 1, justifyContent: 'flex-end' }}>
                                    <DatePicker
                                        label="Start Date"
                                        value={startDate}
                                        onChange={(newValue) => setStartDate(newValue)}
                                        maxDate={endDate}
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            textField: {
                                                sx: { flex: 1, minWidth: { xs: '100%', sm: 140 }, width: { xs: '100%', sm: 'auto' } },
                                                error: !isDateRangeValid,
                                                helperText: !isDateRangeValid ? 'Start date must be before end date' : ''
                                            }
                                        }}
                                    />
                                    <DatePicker
                                        label="End Date"
                                        value={endDate}
                                        onChange={(newValue) => setEndDate(newValue)}
                                        minDate={startDate}
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            textField: {
                                                sx: { flex: 1, minWidth: { xs: '100%', sm: 140 }, width: { xs: '100%', sm: 'auto' } },
                                                error: !isDateRangeValid,
                                                helperText: !isDateRangeValid ? 'End date must be after start date' : ''
                                            }
                                        }}
                                    />
                                    <MDBox sx={{ flex: 1, minWidth: { xs: '100%', sm: 200 }, width: { xs: '100%', sm: 'auto' } }}>
                                        <RideSelector
                                            fullWidth
                                            label="Ride"
                                            name="ride_id"
                                            value={rideId}
                                            onChange={(selectedOption) => { setRideId(selectedOption) }}
                                            isClearable
                                        />
                                    </MDBox>
                                    {/* Vendor selector removed from global filters */}
                                    {/* <MDButton
                                        variant="contained"
                                        color="primary"
                                        onClick={handleDateRangeChange}
                                        startIcon={<Icon>refresh</Icon>}
                                        disabled={isLoading || !isDateRangeValid}
                                        sx={{ minWidth: 100 }}
                                    >
                                        Apply
                                    </MDButton> */}
                                    {/* <MDButton
                                        variant="outlined"
                                        color="primary"
                                        onClick={handleExportData}
                                        startIcon={<Icon>download</Icon>}
                                        disabled={isLoading || !isDateRangeValid || !dashboardData}
                                        sx={{ minWidth: 100 }}
                                    >
                                        Export
                                    </MDButton> */}
                                </MDBox>
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>

                <Grid container spacing={4}>

                    {/* WORK ORDER SECTION */}
                    <SectionHeader
                        title="🔧 Work Orders Management"
                        subtitle="Track work order progress and completion status"
                    />

                    {/* Work Order KPIs */}
                    <Grid item xs={12} md={3}>
                        <ComplexStatisticsCard
                            icon="engineering"
                            title="Total Work Orders"
                            color="primary"
                            count={kpiValues.totalWorkOrders}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <ComplexStatisticsCard
                            icon="hourglass_bottom"
                            title="Waiting for Parts"
                            color="warning"
                            count={kpiValues.waitingForParts}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <ComplexStatisticsCard
                            icon="check_circle"
                            title="Completed Orders"
                            color="success"
                            count={kpiValues.completedWorkOrders}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <ComplexStatisticsCard
                            icon="timer"
                            title="Avg Completion Time"
                            color="dark"
                            count={
                                kpiValues.avgCompletionHours && kpiValues.avgCompletionHours !== 'N/A'
                                    ? `${Math.round(kpiValues.avgCompletionHours)}h`
                                    : 'N/A'
                            }
                        />
                    </Grid>

                    {/* Additional Work Order KPIs */}
                    <Grid item xs={12} md={3}>
                        <ComplexStatisticsCard
                            icon="priority_high"
                            title="Overdue Orders"
                            color="error"
                            count={kpiValues.overdueOrders}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <ComplexStatisticsCard
                            icon="today"
                            title="Due Today"
                            color="warning"
                            count={kpiValues.dueTodayOrders}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <ComplexStatisticsCard
                            icon="schedule"
                            title="Due Soon"
                            color="info"
                            count={kpiValues.dueSoonOrders}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <ComplexStatisticsCard
                            icon="report_problem"
                            title="Critical Open"
                            color="error"
                            count={kpiValues.criticalOpenOrders}
                        />
                    </Grid>

                    {/* Work Order Chart */}
                    <Grid item xs={12}>
                        <WorkOrderProgressChart
                            data={dashboardData?.work_orders || {}}
                            isLoading={isLoading}
                        />
                    </Grid>



                    {/* INVENTORY & SPARE PARTS SECTION */}
                    <SectionHeader
                        title="📦 Inventory & Spare Parts"
                        subtitle="Monitor inventory levels, spare parts usage, and cost trends"
                    />

                    {/* Inventory KPIs */}
                    <Grid item xs={12} md={6}>
                        <ComplexStatisticsCard
                            icon="inventory_2"
                            title="Total Spare Parts"
                            color="primary"
                            count={kpiValues.totalSpareparts}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ComplexStatisticsCard
                            icon="attach_money"
                            title="Stock Value"
                            color="success"
                            count={kpiValues.totalStockValue}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ComplexStatisticsCard
                            icon="warning"
                            title="Below Minimum"
                            color="warning"
                            count={kpiValues.belowMinimum}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ComplexStatisticsCard
                            icon="remove_circle"
                            title="Out of Stock"
                            color="error"
                            count={kpiValues.outOfStock}
                        />
                    </Grid>

                    {/* Inventory Parts Table */}
                    <Grid item xs={12}>
                        <InventoryPartsTable
                            data={dashboardData?.sparepart_usage || {}}
                            isLoading={isLoading}
                        />
                    </Grid>

                    {/* VENDOR PERFORMANCE SECTION */}
                    <SectionHeader
                        title="🤝 Vendor Performance"
                        subtitle="Analyze vendor cost trends and spare part spending"
                    />

                    {/* Vendor Performance Filters */}
                    <Grid item xs={12} mt={-4} mb={1}>
                        <Card sx={{ p: 2 }}>
                            <MDBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                                <MDBox>
                                    <MDTypography variant="h6" fontWeight="medium" color="dark" mb={0.5}>
                                        Filters
                                    </MDTypography>
                                    <MDTypography variant="body2" color="text">
                                        Adjust date range or filter by vendor
                                    </MDTypography>
                                </MDBox>
                                <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap" sx={{ flex: 1, justifyContent: 'flex-end' }}>
                                    <DatePicker
                                        label="Start Date"
                                        value={vendorStartDate}
                                        onChange={(v) => setVendorStartDate(v)}
                                        maxDate={vendorEndDate}
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            textField: {
                                                sx: { flex: 1, minWidth: { xs: '100%', sm: 140 }, width: { xs: '100%', sm: 'auto' } },
                                                error: !isVendorDateRangeValid,
                                                helperText: !isVendorDateRangeValid ? 'Start date must be before end date' : ''
                                            }
                                        }}
                                    />
                                    <DatePicker
                                        label="End Date"
                                        value={vendorEndDate}
                                        onChange={(v) => setVendorEndDate(v)}
                                        minDate={vendorStartDate}
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            textField: {
                                                sx: { flex: 1, minWidth: { xs: '100%', sm: 140 }, width: { xs: '100%', sm: 'auto' } },
                                                error: !isVendorDateRangeValid,
                                                helperText: !isVendorDateRangeValid ? 'End date must be after start date' : ''
                                            }
                                        }}
                                    />
                                    <MDBox sx={{ flex: 1, minWidth: { xs: '100%', sm: 240 }, width: { xs: '100%', sm: 'auto' } }}>
                                        <VendorSelector
                                            fullWidth
                                            label="Vendors"
                                            name="vendor_filter_ids"
                                            value={vendorFilter}
                                            onChange={(values) => setVendorFilter(values)}
                                            placeholder="Select vendors"
                                        />
                                    </MDBox>
                                </MDBox>
                            </MDBox>
                        </Card>
                    </Grid>

                    {/* Vendor Performance KPIs */}
                    {(() => {
                        const summary = vendorPerfData?.summary || {};
                        const formatPercent = (val) => (val === null || val === undefined ? 'N/A' : `${val.toFixed(2)}%`);
                        return (
                            <>
                                <Grid item xs={12} md={3}>
                                    <ComplexStatisticsCard
                                        icon="assignment"
                                        title="Price Entries"
                                        color="primary"
                                        count={vendorPerfLoading ? '-' : (summary.total_price_entries || 0)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <ComplexStatisticsCard
                                        icon="category"
                                        title="Distinct Spareparts"
                                        color="info"
                                        count={vendorPerfLoading ? '-' : (summary.distinct_spareparts || 0)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <ComplexStatisticsCard
                                        icon="attach_money"
                                        title="Total Cost"
                                        color="success"
                                        count={vendorPerfLoading ? '-' : formatCurrency(summary.total_cost_sum || 0)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <ComplexStatisticsCard
                                        icon="trending_up"
                                        title="Avg Cost"
                                        color="dark"
                                        count={vendorPerfLoading ? '-' : formatCurrency(summary.average_cost || 0)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <ComplexStatisticsCard
                                        icon="arrow_downward"
                                        title="Min Cost"
                                        color="secondary"
                                        count={vendorPerfLoading ? '-' : formatCurrency(summary.min_cost || 0)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <ComplexStatisticsCard
                                        icon="arrow_upward"
                                        title="Max Cost"
                                        color="secondary"
                                        count={vendorPerfLoading ? '-' : formatCurrency(summary.max_cost || 0)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <ComplexStatisticsCard
                                        icon="sync_alt"
                                        title="Cost Volatility"
                                        color="warning"
                                        count={vendorPerfLoading ? '-' : formatPercent(summary.cost_volatility_percent || 0)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <ComplexStatisticsCard
                                        icon="pending_actions"
                                        title="Avg Lead Time"
                                        color="info"
                                        count={vendorPerfLoading ? '-' : (summary.average_lead_time_days ? `${summary.average_lead_time_days.toFixed(2)}d` : 'N/A')}
                                    />
                                </Grid>
                            </>
                        );
                    })()}

                    {/* Monthly Cost Trend (Bar Chart) & Top Spareparts */}
                   
                    <Grid item xs={12} >
                        <Card sx={{ height: '100%' }}>
                            <MDBox display="flex" alignItems="center" pt={2} px={2} pb={1}>
                                <MDBox
                                    variant="gradient"
                                    bgColor="success"
                                    color="white"
                                    coloredShadow="success"
                                    borderRadius="xl"
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    width="4rem"
                                    height="4rem"
                                    mt={-3}
                                    mr={2}
                                >
                                    <Icon fontSize="medium" color="inherit">star</Icon>
                                </MDBox>
                                <MDBox>
                                    <MDTypography variant="h6" textTransform="capitalize" color="dark">
                                        Top Spareparts by Cost
                                    </MDTypography>
                                    <MDBox mb={2}>
                                        <MDTypography component="div" variant="button" color="text">
                                            Highest spending parts
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            </MDBox>
                            <MDBox px={2} pb={2}>
                                {vendorPerfLoading ? (
                                    <MDBox display="flex" justifyContent="center" alignItems="center" py={6}>
                                        <Loading size={40} />
                                    </MDBox>
                                ) : (
                                    <TableContainer component={Paper} sx={{ maxHeight: 360, boxShadow: 1, width: '100%' }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead style={{ display: "table-header-group" }}>
                                                <TableRow>
                                                    <TableCell><strong>Sparepart</strong></TableCell>
                                                    <TableCell align="right"><strong>Total Cost</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(vendorPerfData?.top_spareparts_by_cost || []).length > 0 ? (
                                                    vendorPerfData.top_spareparts_by_cost.map((row, idx) => (
                                                        <TableRow key={idx}>
                                                            <TableCell>{row.sparepart_name}</TableCell>
                                                            <TableCell align="right">{formatCurrency(row.total_cost || 0)}</TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                                                            <MDTypography variant="body2" color="text">No data</MDTypography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                    <Grid item xs={12} mt={2}>
                        {vendorPerfLoading ? (
                            <Card sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 420 }}>
                                <Loading size={40} />
                            </Card>
                        ) : (
                            <ReportsBarChart
                                color="info"
                                title="Monthly Cost Trend"
                                description="Total cost per month"
                                chart={{
                                    labels: (vendorPerfData?.monthly_cost_trend || []).map(r => r.month),
                                    datasets: {
                                        label: 'Total Cost',
                                        data: (vendorPerfData?.monthly_cost_trend || []).map(r => parseFloat(r.total_cost || 0).toFixed(2))
                                    }
                                }}
                            />
                        )}
                    </Grid>
                </Grid>
            </MDBox>
        </LocalizationProvider>
    );
};

// PropTypes
WorkOrderProgressChart.displayName = 'WorkOrderProgressChart';
InventoryPartsTable.displayName = 'InventoryPartsTable';

export default DashboardOverview;
